import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { RecordNav } from "../components/RecordNav";
import { PostingDateHint } from "../components/PostingDateHint";
import { MARGIN_DOC_TYPES, docMargin, lineMargin, MarginBadge } from "../lib/margin";

const docKey = (d) => d.doc_no;
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, FloppyDisk, CaretDown, CaretRight, Plus, Trash, ArrowsClockwise,
  SealCheck, FilePdf, EnvelopeSimple, Sparkle, Note as NoteIcon, ListChecks, CurrencyDollar, Storefront, Truck, ArrowUUpLeft, Paperclip,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, money, moneyIn, formatApiErrorDetail, localToday } from "../lib/api";
import { useAutoSave, AutoSaveIndicator } from "../lib/useAutoSave";
import { StatusChip, ConfirmDialog } from "../components/shared";
import { AddressFields, IncotermsFields } from "../components/AddressFields";
import { COUNTRIES, CURRENCIES, gstRateFor } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DocNoField } from "../components/DocNoField";
import { EmailMessageBox } from "../components/POEmailMessage";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Field, ReadOnly, RibbonBtn, PaneRow } from "../components/DocumentCardParts";
import { itemLineDescription, LinePartNo, isMobileViewport, UseItemDescription, ItemPicker } from "../components/LineItemText";
import { AttachmentsPane } from "../components/AttachmentsPane";
import { usePerms } from "../lib/perms";
import { ViewOnlyBadge } from "../components/Permission";
import { EmailAttachmentPicker } from "../components/EmailAttachmentPicker";
import { ReversePostedLinesDialog } from "../components/ReversePostedLinesDialog";
import { RefundCreditDialog, ApplyCreditDialog } from "../components/CreditActionsDialogs";
import { Checkbox } from "../components/ui/checkbox";
const DOC_LABEL = {
  Quote: "Sales Quote", Order: "Sales Order", Invoice: "Sales Invoice",
  CreditMemo: "Sales CR/Adj Note", ReturnOrder: "Sales Return Order",
};

function Section({ title, subtitle, defaultOpen = true, mobileCollapsed = false, children }) {
  const [open, setOpen] = useState(defaultOpen && !(mobileCollapsed && isMobileViewport()));
  return (
    <div className="border border-border bg-card mb-4" data-testid={`section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-secondary/50 transition-colors"
        data-testid={`section-toggle-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      >
        {open ? <CaretDown size={16} className="text-primary" /> : <CaretRight size={16} className="text-muted-foreground" />}
        <span className="font-bold text-sm tracking-tight">{title}</span>
        {subtitle && <span className="text-xs text-muted-foreground ml-1">— {subtitle}</span>}
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );
}

export default function SalesOrderCard() {
  const { id } = useParams();
  const isNew = id === "new";
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);

  const backTab = sp.get("tab") || "orders";
  const goBack = () => navigate(`/sales?tab=${backTab}`);

  const { data: doc, isLoading } = useQuery({
    queryKey: ["sales-order", id],
    queryFn: async () => (await api.get(`/sales-orders/${id}`)).data,
    enabled: !isNew,
  });
  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const { data: items = [] } = useQuery({ queryKey: ["items"], queryFn: async () => (await api.get("/items")).data });
  const { data: companyProfile } = useQuery({ queryKey: ["company-profile"], queryFn: async () => (await api.get("/company-profile")).data });
  const marginThreshold = Number(companyProfile?.low_margin_pct ?? 20);
  const { data: locations = [] } = useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get("/locations")).data });
  const { data: salespeople = [] } = useQuery({ queryKey: ["salespeople"], queryFn: async () => (await api.get("/salespeople")).data });
  const { data: fxData } = useQuery({ queryKey: ["fx-rates"], queryFn: async () => (await api.get("/fx/rates")).data, staleTime: 60 * 60 * 1000 });

  const docType = isNew ? (sp.get("type") || "Order") : (doc?.doc_type || "Order");
  const hasPostings = !isNew && docType === "Order" && (doc?.lines || []).some((l) => (l.qty_shipped || 0) > 0 || (l.qty_invoiced || 0) > 0);
  const { can, isAdmin } = usePerms();
  const viewOnly = !can("sales.edit");
  const canPost = can("sales.post");
  const readOnly = viewOnly || !!doc?.posted || !!doc?.archived || hasPostings;
  const showQtyCols = docType === "Order" && !isNew;
  const showLead = docType === "Quote";
  const lineLead = (l) => Number(l.lead_time_days || items.find((x) => x.id === l.item_id)?.lead_time_days || 0);
  const estDelivery = (l) => {
    const d = new Date(`${form.date}T00:00:00`);
    if (!form.date || Number.isNaN(d.getTime()) || !lineLead(l)) { return "—"; }
    d.setDate(d.getDate() + Math.round(lineLead(l)));
    return d.toLocaleDateString("en-CA");
  };

  const [form, setForm] = useState({
    customer_id: "", date: today, posting_date: today, due_date: "", requested_delivery_date: "",
    valid_until: "", external_doc_no: "", ship_to_name: "", ship_to_address: "", tax_rate: 10,
    ship_to_address2: "", ship_to_city: "", ship_to_state: "", ship_to_postcode: "", ship_to_country: "",
    bill_to_address: "", bill_to_address2: "", bill_to_city: "", bill_to_state: "", bill_to_postcode: "", bill_to_country: "",
    incoterms: "", incoterms_location: "",
    salesperson: "", location_code: "", currency: "",
    freight_name: "", freight_account_no: "", freight_address: "", freight_address2: "", freight_address3: "",
    freight_contact: "", freight_postcode: "", freight_city: "", freight_county: "", freight_country: "",
    freight_phone: "", freight_fax: "", freight_telex: "", freight_email: "", freight_location_code: "",
    freight_shipment_date: "", freight_shipping_advice: "", freight_delivery_instruction: "",
  });
  const [lines, setLines] = useState([{ item_id: "", description: "", quantity: 1, unit_price: 0 }]);

  useEffect(() => {
    if (doc) {
      setForm({
        customer_id: doc.customer_id || "", date: doc.date || today, posting_date: doc.posting_date || doc.date || today,
        due_date: doc.due_date || "", requested_delivery_date: doc.requested_delivery_date || "",
        valid_until: doc.valid_until || "", external_doc_no: doc.external_doc_no || "",
        ship_to_name: doc.ship_to_name || "", ship_to_address: doc.ship_to_address || "", tax_rate: doc.tax_rate ?? 0,
        ship_to_address2: doc.ship_to_address2 || "", ship_to_city: doc.ship_to_city || "", ship_to_state: doc.ship_to_state || "",
        ship_to_postcode: doc.ship_to_postcode || "", ship_to_country: doc.ship_to_country || "",
        bill_to_address: doc.bill_to_address || "", bill_to_address2: doc.bill_to_address2 || "", bill_to_city: doc.bill_to_city || "",
        bill_to_state: doc.bill_to_state || "", bill_to_postcode: doc.bill_to_postcode || "", bill_to_country: doc.bill_to_country || "",
        incoterms: doc.incoterms || "", incoterms_location: doc.incoterms_location || "",
        salesperson: doc.salesperson || "", location_code: doc.location_code || "",
        currency: doc.currency || "", return_to_stock: doc.return_to_stock !== false,
        freight_name: doc.freight_name || "", freight_account_no: doc.freight_account_no || "",
        freight_address: doc.freight_address || "", freight_address2: doc.freight_address2 || "", freight_address3: doc.freight_address3 || "",
        freight_contact: doc.freight_contact || "", freight_postcode: doc.freight_postcode || "", freight_city: doc.freight_city || "",
        freight_county: doc.freight_county || "", freight_country: doc.freight_country || "", freight_phone: doc.freight_phone || "",
        freight_fax: doc.freight_fax || "", freight_telex: doc.freight_telex || "", freight_email: doc.freight_email || "",
        freight_location_code: doc.freight_location_code || "", freight_shipment_date: doc.freight_shipment_date || "",
        freight_shipping_advice: doc.freight_shipping_advice || "", freight_delivery_instruction: doc.freight_delivery_instruction || "",
      });
      setLines((doc.lines || []).length ? doc.lines.map((l) => ({ ...l })) : [{ item_id: "", description: "", quantity: 1, unit_price: 0 }]);
    }
  }, [doc]); // eslint-disable-line react-hooks/exhaustive-deps

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    setForm((f) => ({
      ...f, customer_id: cid,
      ...(c?.currency && (isNew || !f.currency) ? { currency: c.currency } : {}),
      ship_to_name: f.ship_to_name || c?.name || "",
      ship_to_address: f.ship_to_address || c?.ship_to_address || c?.address || "",
      ship_to_address2: f.ship_to_address2 || c?.ship_to_address2 || c?.address2 || "",
      ship_to_city: f.ship_to_city || c?.ship_to_city || c?.city || "",
      ship_to_state: f.ship_to_state || c?.ship_to_state || c?.state || "",
      ship_to_postcode: f.ship_to_postcode || c?.ship_to_postcode || c?.postcode || "",
      ship_to_country: f.ship_to_country || c?.ship_to_country || c?.country || "",
      bill_to_address: f.bill_to_address || c?.bill_to_address || c?.address || "",
      bill_to_address2: f.bill_to_address2 || c?.bill_to_address2 || c?.address2 || "",
      bill_to_city: f.bill_to_city || c?.bill_to_city || c?.city || "",
      bill_to_state: f.bill_to_state || c?.bill_to_state || c?.state || "",
      bill_to_postcode: f.bill_to_postcode || c?.bill_to_postcode || c?.postcode || "",
      bill_to_country: f.bill_to_country || c?.bill_to_country || c?.country || "",
    }));
  };
  const pickItem = (idx, itemId) => {
    const it = items.find((x) => x.id === itemId);
    setLines((ls) => ls.map((l, i) => i === idx ? { ...l, item_id: itemId, description: itemLineDescription(it) || l.description, unit_price: it?.unit_price ?? l.unit_price, unit_cost: it?.cost ?? l.unit_cost ?? 0, lead_time_days: it?.lead_time_days ?? l.lead_time_days ?? 0 } : l));
  };
  const setLine = (idx, k, v) => setLines((ls) => ls.map((l, i) => i === idx ? { ...l, [k]: v } : l));
  const addLine = () => setLines((ls) => [...ls, { item_id: "", description: "", quantity: 1, unit_price: 0 }]);
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));

  const subtotal = useMemo(() => lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0), [lines]);
  const selCustomer = customers.find((c) => c.id === form.customer_id);
  const gstAuto = gstRateFor(selCustomer);
  const taxRate = doc?.posted ? Number(form.tax_rate || 0) : (gstAuto ?? Number(form.tax_rate || 0));
  const tax = doc?.posted ? Number(doc.tax || 0) : subtotal * (taxRate / 100);
  const total = doc?.posted ? Number(doc.total || 0) : subtotal + tax;
  const totalCost = useMemo(() => lines.reduce((s, l) => { const it = items.find((x) => x.id === l.item_id); return s + Number(l.quantity || 0) * Number(it?.cost || 0); }, 0), [lines, items]);
  const gpPct = subtotal > 0 ? ((subtotal - totalCost) / subtotal) * 100 : 0;

  // Currency / AUD conversion. Rate locks at creation (doc.fx_rate_to_aud); new docs use today's live rate.
  const curCode = form.currency || "";
  const fmt = (n) => (curCode ? moneyIn(n, curCode) : money(n));
  const showAud = !!curCode && curCode !== "AUD";
  const liveRate = curCode ? Number(fxData?.rates_to_aud?.[curCode] || 0) : 1;
  const lockedRate = Number(doc?.fx_rate_to_aud || 0);
  const rateToAud = (!isNew && doc?.currency === curCode && lockedRate) ? lockedRate : liveRate;
  const showMargin = MARGIN_DOC_TYPES.includes(docType);
  const margin = showMargin ? docMargin(lines, items, rateToAud || 1) : null;
  const lowMargin = !!margin && margin.pct < marginThreshold;
  const totalAud = rateToAud ? total * rateToAud : 0;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["sales-orders"] });
    qc.invalidateQueries({ queryKey: ["sales-order", id] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["customers"] });
  };

  const payload = () => ({
    ...(isNew ? { doc_no: form.doc_no || "" } : {}),
    customer_id: form.customer_id, date: form.date, posting_date: form.posting_date,
    due_date: form.due_date, requested_delivery_date: form.requested_delivery_date,
    valid_until: docType === "Quote" ? form.valid_until : "", external_doc_no: form.external_doc_no,
    ship_to_name: form.ship_to_name, ship_to_address: form.ship_to_address, tax_rate: taxRate,
    ship_to_address2: form.ship_to_address2, ship_to_city: form.ship_to_city, ship_to_state: form.ship_to_state,
    ship_to_postcode: form.ship_to_postcode, ship_to_country: form.ship_to_country,
    bill_to_address: form.bill_to_address, bill_to_address2: form.bill_to_address2, bill_to_city: form.bill_to_city,
    bill_to_state: form.bill_to_state, bill_to_postcode: form.bill_to_postcode, bill_to_country: form.bill_to_country,
    incoterms: form.incoterms, incoterms_location: form.incoterms_location,
    salesperson: form.salesperson, location_code: form.location_code,
    currency: form.currency || "", return_to_stock: form.return_to_stock !== false,
    freight_name: form.freight_name, freight_account_no: form.freight_account_no,
    freight_address: form.freight_address, freight_address2: form.freight_address2, freight_address3: form.freight_address3,
    freight_contact: form.freight_contact, freight_postcode: form.freight_postcode, freight_city: form.freight_city,
    freight_county: form.freight_county, freight_country: form.freight_country, freight_phone: form.freight_phone,
    freight_fax: form.freight_fax, freight_telex: form.freight_telex, freight_email: form.freight_email,
    freight_location_code: form.freight_location_code, freight_shipment_date: form.freight_shipment_date,
    freight_shipping_advice: form.freight_shipping_advice, freight_delivery_instruction: form.freight_delivery_instruction,
    lines: lines.filter((l) => l.description).map((l) => ({ item_id: l.item_id || "", description: l.description, quantity: Number(l.quantity), unit_price: Number(l.unit_price), lead_time_days: Number(l.lead_time_days || 0), unit_cost: Number(l.unit_cost || 0), applies_to_line_id: l.applies_to_line_id || "" })),
  });

  // ---- auto-save ----
  const createdRef = useRef(false);
  const autoSaveFn = async () => {
    if (isNew) {
      if (createdRef.current) return;
      createdRef.current = true;
      try {
        const res = await api.post("/sales-orders", { doc_type: docType, status: "Open", ...payload() });
        invalidate();
        navigate(`/sales/doc/${res.data.id}?tab=${backTab}`, { replace: true });
        return res.data;
      } catch (e) { createdRef.current = false; throw e; }
    }
    const res = await api.put(`/sales-orders/${id}`, payload());
    invalidate();
    return res.data;
  };
  const { status: autoStatus, flush: autoSaveFlush } = useAutoSave({ active: !readOnly, canSave: !!form.customer_id, createMode: isNew, snapshot: payload(), save: autoSaveFn });

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) return (await api.post("/sales-orders", { doc_type: docType, status: "Open", ...payload() })).data;
      return (await api.put(`/sales-orders/${id}`, payload())).data;
    },
    onSuccess: (res) => {
      invalidate();
      toast.success(isNew ? `${DOC_LABEL[docType]} ${res.doc_no} created` : "Document saved");
      if (isNew) navigate(`/sales/doc/${res.id}?tab=${backTab}`, { replace: true });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const [confirm, setConfirm] = useState(null); // {kind}
  const [reverseOpen, setReverseOpen] = useState(false);
  const [creditAction, setCreditAction] = useState(null);
  const [delPosted, setDelPosted] = useState(null);
  const deletePosted = useMutation({
    mutationFn: async () => (await api.post(`/sales-orders/${id}/delete-posted`, { reason: delPosted })).data,
    onSuccess: (r) => { invalidate(); toast.success(`${r.doc_no} deleted${r.payments_removed ? ` · ${r.payments_removed} payment(s) reversed` : ""}`); navigate("/sales?tab=invoices"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const convertToOrder = useMutation({ mutationFn: async () => (await api.post(`/sales-orders/${id}/convert-to-order`)).data, onSuccess: (r) => { invalidate(); toast.success(`Converted to order ${r.doc_no}`); navigate(`/sales/doc/${r.id}?tab=orders`); }, onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)) });
  const convertToInvoice = useMutation({ mutationFn: async () => (await api.post(`/sales-orders/${id}/convert-to-invoice`)).data, onSuccess: (r) => { invalidate(); toast.success(`Converted to invoice ${r.doc_no}`); navigate(`/sales/doc/${r.id}?tab=invoices`); }, onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)) });
  const post = useMutation({ mutationFn: async () => (await api.post(`/sales-orders/${id}/post`)).data, onSuccess: (r) => { invalidate(); toast.success(r.shipment ? `Posted · shipment ${r.shipment.shipment_no}` : "Document posted"); }, onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)) });
  const emailDoc = useMutation({ mutationFn: async () => (await api.post(`/sales-orders/${id}/send-email`, { recipients: [...selectedRecipients, ...manualList], attachment_ids: emailAttachIds, message: emailMessage })).data, onSuccess: (r) => { setEmailOpen(false); toast.success(`Emailed to ${(r.sent_to || []).join(", ")}`); }, onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)) });
  const del = useMutation({ mutationFn: async () => api.delete(`/sales-orders/${id}`), onSuccess: () => { invalidate(); toast.success("Deleted"); goBack(); } });

  // ---- BC posting (ship / invoice / ship & invoice) ----
  const buildPostLines = (ls, action) => (ls || []).map((l) => {
    const q = Number(l.quantity || 0), sh = Number(l.qty_shipped || 0), inv = Number(l.qty_invoiced || 0);
    const toShip = Math.round(Math.max(0, q - sh) * 10000) / 10000;
    const shippedAfter = action === "invoice" ? sh : sh + toShip;
    const toInv = Math.round(Math.max(0, shippedAfter - inv) * 10000) / 10000;
    return { line_id: l.line_id, description: l.description, quantity: q, qty_shipped: sh, qty_invoiced: inv, qty_to_ship: toShip, qty_to_invoice: toInv };
  });
  const [postOpen, setPostOpen] = useState(false);
  const [postAction, setPostAction] = useState("ship_and_invoice");
  const [postLines, setPostLines] = useState([]);
  const extDocRequired = !readOnly && (docType === "Order" || docType === "Invoice");
  const extDocOk = () => {
    if (form.external_doc_no?.trim()) return true;
    toast.error("Enter the External Document No. before posting.");
    document.querySelector('[data-testid="card-ext-doc"]')?.focus();
    return false;
  };
  const openPost = async () => { if (!extDocOk()) return; await autoSaveFlush?.(); const fresh = (await api.get(`/sales-orders/${id}`)).data; setPostAction("ship_and_invoice"); setPostLines(buildPostLines(fresh?.lines, "ship_and_invoice")); setPostOpen(true); };
  const changeAction = (a) => { setPostAction(a); setPostLines(buildPostLines(doc?.lines, a)); };
  const setPostLine = (lid, k, v) => setPostLines((ls) => ls.map((l) => l.line_id === lid ? { ...l, [k]: v } : l));
  const postSO = useMutation({
    mutationFn: async () => (await api.post(`/sales-orders/${id}/post-sales-order`, {
      action: postAction,
      lines: postLines.map((l) => ({ line_id: l.line_id, qty_to_ship: Number(l.qty_to_ship || 0), qty_to_invoice: Number(l.qty_to_invoice || 0) })),
    })).data,
    onSuccess: (r) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["posted-shipments"] });
      const bits = [];
      if (r.shipment) bits.push(`shipment ${r.shipment.shipment_no}`);
      if (r.invoice) bits.push(`invoice ${r.invoice.doc_no}`);
      toast.success(`Posted — ${bits.join(" & ") || "done"}`);
      setPostOpen(false);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  // ---- email confirmation dialog ----
  const [emailOpen, setEmailOpen] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [emailAttachIds, setEmailAttachIds] = useState([]);
  const [manualEmail, setManualEmail] = useState("");
  const manualList = useMemo(() => manualEmail.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean), [manualEmail]);
  const totalRecipients = selectedRecipients.length + manualList.length;
  const { data: emailRecipients } = useQuery({
    queryKey: ["so-email-recipients", id],
    queryFn: async () => (await api.get(`/sales-orders/${id}/email-recipients`)).data,
    enabled: emailOpen && !isNew,
  });
  const recipientOptions = useMemo(() => emailRecipients?.options || [], [emailRecipients]);
  useEffect(() => {
    if (sp.get("email") === "1" && doc?.id) setEmailOpen(true);
  }, [doc?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (emailOpen && recipientOptions.length && selectedRecipients.length === 0) {
      const primary = recipientOptions.find((o) => o.primary) || recipientOptions[0];
      setSelectedRecipients([primary.email]);
    }
    if (!emailOpen && selectedRecipients.length > 0) setSelectedRecipients([]);
    if (!emailOpen && manualEmail) setManualEmail("");
    if (!emailOpen && emailAttachIds.length > 0) setEmailAttachIds([]);
  }, [emailOpen, recipientOptions]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleRecipient = (email) => setSelectedRecipients((rs) => rs.includes(email) ? rs.filter((e) => e !== email) : [...rs, email]);
  const [emailMessage, setEmailMessage] = useState("");
  const [messageTouched, setMessageTouched] = useState(false);
  useEffect(() => {
    if (!emailOpen) { setEmailMessage(""); setMessageTouched(false); return; }
    if (!messageTouched) setEmailMessage(emailRecipients?.message || "");
  }, [emailOpen, emailRecipients?.message]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- payment on posted invoice ----
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("Bank Transfer");
  const openPayment = () => { setPayAmount(String(Math.round(((doc?.total || 0) - (doc?.amount_paid || 0) - (doc?.credit_applied || 0)) * 100) / 100)); setPayMethod("Bank Transfer"); setPayOpen(true); };
  const recordPayment = useMutation({
    mutationFn: async () => (await api.post(`/sales-orders/${id}/payments`, { amount: Number(payAmount), method: payMethod })).data,
    onSuccess: () => { invalidate(); setPayOpen(false); toast.success("Payment recorded"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const correctInvoice = useMutation({
    mutationFn: async () => (await api.post(`/sales-orders/${id}/correct`)).data,
    onSuccess: (r) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["items"] });
      toast.success(`Corrective credit memo ${r.credit_memo.doc_no} created${r.reopened_order ? ` · ${r.reopened_order} reopened` : ""}`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const postReturn = useMutation({
    mutationFn: async () => (await api.post(`/sales-orders/${id}/post-return`)).data,
    onSuccess: (r) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["posted-return-receipts"] });
      toast.success(`Return posted — credit ${r.credit_memo.doc_no} · receipt ${r.receipt.receipt_no}`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const [poResult, setPoResult] = useState(null);
  const makePOs = useMutation({
    mutationFn: async () => (await api.post(`/sales-orders/${id}/create-purchase-orders-by-vendor`)).data,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      qc.invalidateQueries({ queryKey: ["vendors"] });
      setPoResult(r);
      toast.success(`Created ${r.count} purchase order${r.count === 1 ? "" : "s"}`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (!isNew && isLoading) {
    return <div className="flex items-center justify-center py-24"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  const title = isNew ? `New ${DOC_LABEL[docType]}` : `${doc.doc_no} · ${doc.customer_name}`;
  const status = doc?.status || "Open";

  return (
    <div className="animate-fade-up pb-10" data-testid="sales-order-card" onBlur={autoSaveFlush}>
      {/* Top bar */}
      <div className="flex items-center gap-3 mb-1">
        <button onClick={goBack} className="p-1.5 -ml-1.5 text-muted-foreground hover:text-primary transition-colors" data-testid="card-back"><ArrowLeft size={18} weight="bold" /></button>
        {doc?.posted && <SealCheck size={16} className="text-emerald-600" weight="fill" />}
      </div>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="card-title">{title}</h1>
          <ViewOnlyBadge show={viewOnly && !isNew} />
          <StatusChip status={status} />
          {lowMargin && <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold border rounded-sm border-red-200 bg-red-50 text-red-700" data-testid="card-low-margin-chip">Low margin {margin.pct.toFixed(1)}%</span>}
          {doc?.shipping_status && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold border rounded-sm border-blue-200 bg-blue-50 text-blue-700" data-testid="card-shipping-status">
              <Truck size={12} weight="bold" /> {doc.shipping_status}
            </span>
          )}
        </div>
        {!isNew && doc && <RecordNav source="sales-orders" currentId={id} sortKey={docKey} basePath="/sales/doc" testId="sales-nav"
          filter={(o) => o.doc_type === doc.doc_type && !!o.posted === !!doc.posted && !!o.archived === !!doc.archived} />}
      </div>

      {doc?.doc_type === "CreditMemo" && doc?.corrects_doc_no && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 border border-amber-200 bg-amber-50 text-amber-800 text-sm rounded-sm" data-testid="audit-reverses">
          <ArrowUUpLeft size={15} weight="bold" />
          <span>Corrective credit memo — reverses{" "}
            {doc.reverses_id
              ? <button onClick={() => navigate(`/sales/doc/${doc.reverses_id}`)} className="font-semibold underline hover:text-amber-900" data-testid="audit-reverses-link">{doc.corrects_doc_no}</button>
              : <span className="font-semibold">{doc.corrects_doc_no}</span>}
            {doc.corrected_by ? ` · by ${doc.corrected_by}` : ""}{doc.corrected_at ? ` on ${doc.corrected_at.slice(0, 10)}` : ""}
          </span>
        </div>
      )}
      {doc?.doc_type === "Invoice" && doc?.corrected && doc?.corrects_doc_no && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 border border-rose-200 bg-rose-50 text-rose-800 text-sm rounded-sm" data-testid="audit-corrected">
          <ArrowUUpLeft size={15} weight="bold" />
          <span>Corrected — reversed by credit memo{" "}
            {doc.reversed_by_id
              ? <button onClick={() => navigate(`/sales/doc/${doc.reversed_by_id}`)} className="font-semibold underline hover:text-rose-900" data-testid="audit-corrected-link">{doc.corrects_doc_no}</button>
              : <span className="font-semibold">{doc.corrects_doc_no}</span>}
            {doc.corrected_by ? ` · by ${doc.corrected_by}` : ""}{doc.corrected_at ? ` on ${doc.corrected_at.slice(0, 10)}` : ""}
          </span>
        </div>
      )}

      {/* Ribbon */}
      <div className="flex items-center gap-1 flex-wrap border border-border bg-card px-2 py-1.5 mb-5 rounded-sm" data-testid="card-ribbon">
        {!readOnly && (
          <>
            <RibbonBtn icon={FloppyDisk} label={isNew ? "Create" : "Save"} onClick={() => save.mutate()} disabled={!form.customer_id || save.isPending} primary testid="ribbon-save" />
            <AutoSaveIndicator status={autoStatus} isNew={isNew} testid="sales-autosave" />
          </>
        )}
        {!isNew && !readOnly && docType === "Quote" && (
          <RibbonBtn icon={ArrowsClockwise} label="Make Order" onClick={() => setConfirm({ kind: "order" })} testid="ribbon-convert-order" />
        )}
        {!isNew && canPost && !doc?.archived && docType === "Order" && (
          <RibbonBtn icon={Truck} label="Post" onClick={openPost} testid="ribbon-post-order" />
        )}
        {!isNew && can("purchasing.post") && !doc?.archived && docType === "Order" && (
          <RibbonBtn icon={Storefront} label="Make Purchase Orders" onClick={() => makePOs.mutate()} disabled={makePOs.isPending} testid="ribbon-make-pos" />
        )}
        {!isNew && canPost && !doc?.archived && docType === "ReturnOrder" && (
          <RibbonBtn icon={ArrowUUpLeft} label="Post Return" onClick={() => setConfirm({ kind: "return" })} testid="ribbon-post-return" />
        )}
        {!isNew && docType === "CreditMemo" && doc?.posted && canPost && doc?.credit_remaining > 0.009 && (
          <>
            <RibbonBtn icon={CurrencyDollar} label="Refund" onClick={() => setCreditAction("refund")} testid="ribbon-refund" />
            <RibbonBtn icon={ArrowsClockwise} label="Apply to Invoice" onClick={() => setCreditAction("apply")} testid="ribbon-apply-credit" />
          </>
        )}
        {!isNew && !readOnly && docType === "CreditMemo" && (
          <RibbonBtn icon={ArrowUUpLeft} label="Get Posted Document Lines to Reverse" onClick={async () => { await autoSaveFlush?.(); setReverseOpen(true); }} testid="ribbon-get-posted-lines" />
        )}
        {!isNew && !readOnly && canPost && (docType === "Invoice" || docType === "CreditMemo") && (
          <RibbonBtn icon={SealCheck} label="Post" onClick={() => (docType === "CreditMemo" ? setConfirm({ kind: "post-cm" }) : extDocOk() && Promise.resolve(autoSaveFlush?.()).then(() => post.mutate()))} testid="ribbon-post" />
        )}
        {!isNew && canPost && docType === "Invoice" && !doc?.archived && status !== "Paid" && (doc?.total || 0) - (doc?.amount_paid || 0) - (doc?.credit_applied || 0) > 0.009 && (
          <RibbonBtn icon={CurrencyDollar} label="Record Payment" onClick={openPayment} testid="ribbon-payment" />
        )}
        {!isNew && canPost && docType === "Invoice" && doc?.posted && !doc?.corrected && !(doc?.amount_paid > 0) && !(doc?.amount_credited > 0) && (
          <RibbonBtn icon={ArrowUUpLeft} label="Correct" onClick={() => setConfirm({ kind: "correct" })} testid="ribbon-correct" />
        )}
        {!isNew && (docType === "Quote" || docType === "Order" || docType === "Invoice" || docType === "CreditMemo") && (
          <RibbonBtn icon={FilePdf} label="Print" onClick={() => window.open(`${API}/sales-orders/${id}/pdf`, "_blank")} testid="ribbon-pdf" />
        )}
        {!isNew && canPost && (docType === "Quote" || docType === "Order" || docType === "Invoice") && (
          <RibbonBtn icon={EnvelopeSimple} label={docType === "Order" ? "Email Confirmation" : docType === "Quote" ? "Email Quote" : "Send"} onClick={() => setEmailOpen(true)} disabled={emailDoc.isPending} testid="ribbon-email" />
        )}
        {!isNew && !readOnly && can("sales.delete") && (
          <RibbonBtn icon={Trash} label="Delete" onClick={() => setConfirm({ kind: "delete" })} testid="ribbon-delete" />
        )}
        {!isNew && docType === "Invoice" && doc?.posted && !doc?.corrected && !(doc?.amount_credited > 0) && can("sales.delete_posted") && (
          <RibbonBtn icon={Trash} label="Delete Posted Invoice" onClick={() => setDelPosted("")} testid="ribbon-delete-posted" />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: document sections */}
        <div className="lg:col-span-2">
          <Section title="General">
            {isNew && !readOnly && (
              <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5" data-testid="required-legend">
                <span className="text-red-500 font-bold">*</span> Required fields are marked and highlighted until completed.
              </p>
            )}
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-3 md:gap-x-6 gap-y-3">
              {isNew && <DocNoField counter={`sales_${docType}`} value={form.doc_no || ""} onChange={(v) => setF("doc_no", v)} testid="card-doc-no" />}
              <Field label="Customer No." required={!readOnly} highlight={isNew && !readOnly && !form.customer_id}>
                {readOnly ? <ReadOnly value={selCustomer?.code || doc.customer_name} /> : (
                  <Select value={form.customer_id} onValueChange={pickCustomer}>
                    <SelectTrigger className="rounded-sm font-mono" data-testid="card-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.code ? `${c.code} · ` : ""}{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Customer Name"><ReadOnly value={selCustomer?.name || doc?.customer_name} /></Field>
              <Field label="External Document No." required={extDocRequired} highlight={extDocRequired && !form.external_doc_no?.trim()}>
                {readOnly ? <ReadOnly value={form.external_doc_no} /> : <Input data-testid="card-ext-doc" value={form.external_doc_no} onChange={(e) => setF("external_doc_no", e.target.value)} className="rounded-sm" placeholder="Customer PO / reference" />}
              </Field>

              <p className="overline-label col-span-2 xl:col-span-3 pt-2 border-t border-border/60 text-primary">Sell-to</p>
              <Field label="Address"><ReadOnly value={selCustomer?.address} /></Field>
              <Field label="Address 2"><ReadOnly value={selCustomer?.address2} /></Field>
              <Field label="City"><ReadOnly value={selCustomer?.city} /></Field>
              <Field label="Postcode"><ReadOnly value={selCustomer?.postcode} /></Field>
              <Field label="State / Province"><ReadOnly value={selCustomer?.state} /></Field>
              <Field label="Country/Region Code"><ReadOnly value={selCustomer?.country} /></Field>
              <Field label="Contact No."><ReadOnly value={selCustomer?.code} /></Field>
              <Field label="Phone No."><ReadOnly value={selCustomer?.phone} /></Field>
              <Field label="Email"><ReadOnly value={selCustomer?.email} /></Field>

              <p className="overline-label col-span-2 xl:col-span-3 pt-2 border-t border-border/60 text-primary">Dates</p>
              <Field label="Document Date" required={!readOnly} highlight={isNew && !readOnly && !form.date}>
                {readOnly ? <ReadOnly value={form.date} /> : <Input type="date" data-testid="card-date" value={form.date} onChange={(e) => setF("date", e.target.value)} className="rounded-sm" />}
              </Field>
              <Field label="Posting Date">
                {readOnly ? <ReadOnly value={form.posting_date} /> : <Input type="date" value={form.posting_date} onChange={(e) => setF("posting_date", e.target.value)} className="rounded-sm" data-testid="card-posting-date" />}
                {!isNew && !viewOnly && docType !== "Quote" && <PostingDateHint doc={{ ...doc, posting_date: form.posting_date }} kind="sales-orders" isAdmin={isAdmin} onDone={() => { setF("posting_date", localToday()); invalidate(); }} />}
              </Field>
              <Field label="Due Date">
                {readOnly ? <ReadOnly value={form.due_date} /> : <Input type="date" value={form.due_date} onChange={(e) => setF("due_date", e.target.value)} className="rounded-sm" />}
              </Field>
              <Field label="Requested Delivery Date">
                {readOnly ? <ReadOnly value={form.requested_delivery_date} /> : <Input type="date" value={form.requested_delivery_date} onChange={(e) => setF("requested_delivery_date", e.target.value)} className="rounded-sm" />}
              </Field>
              {docType === "Quote" && (
                <Field label="Valid Until">
                  {readOnly ? <ReadOnly value={form.valid_until} /> : <Input type="date" data-testid="card-valid-until" value={form.valid_until} onChange={(e) => setF("valid_until", e.target.value)} className="rounded-sm" />}
                </Field>
              )}

              <p className="overline-label col-span-2 xl:col-span-3 pt-2 border-t border-border/60 text-primary">Invoicing</p>
              <Field label="Salesperson Code">
                {readOnly ? <ReadOnly value={form.salesperson} /> : (
                  <Select value={form.salesperson || "none"} onValueChange={(v) => setF("salesperson", v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-sm" data-testid="card-salesperson"><SelectValue placeholder="Select salesperson" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {salespeople.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Incoterms Code"><ReadOnly value={[form.incoterms, form.incoterms_location].filter(Boolean).join(" — ")} /></Field>
              <Field label="Location Code">
                {readOnly ? <ReadOnly value={form.location_code} /> : (
                  <Select value={form.location_code || "none"} onValueChange={(v) => setF("location_code", v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-sm" data-testid="card-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {locations.map((l) => <SelectItem key={l.id} value={l.code}>{l.code} · {l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Status"><ReadOnly value={status} /></Field>
              {docType === "CreditMemo" && (
                <>
                  <Field label="Applies-to Doc. No.">
                    {doc?.applies_to_id
                      ? <button onClick={() => navigate(`/sales/doc/${doc.applies_to_id}`)} className="h-9 w-full flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm font-mono text-primary underline" data-testid="cm-applies-to">{doc.applies_to_doc_no}</button>
                      : <ReadOnly value="— use Get Posted Document Lines to Reverse" />}
                  </Field>
                  <Field label="Return Items to Stock">
                    <label className="h-9 flex items-center gap-2 text-sm">
                      <Checkbox checked={form.return_to_stock !== false} disabled={readOnly} onCheckedChange={(v) => setF("return_to_stock", !!v)} data-testid="cm-return-to-stock" />
                      {form.return_to_stock !== false ? "Yes — item quantities go back into stock on posting" : "No"}
                    </label>
                  </Field>
                  {doc?.posted && <Field label="Credit Applied to Invoice"><ReadOnly value={fmt(doc.credit_applied || 0)} /></Field>}
                  {doc?.posted && <Field label="Credit Remaining"><div className={`h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular font-semibold ${doc.credit_remaining > 0 ? "text-emerald-700" : ""}`} data-testid="cm-credit-remaining">{fmt(doc.credit_remaining || 0)}</div></Field>}
                  {doc?.credit_refunded > 0 && <Field label="Refunded"><ReadOnly value={fmt(doc.credit_refunded)} /></Field>}
                  {(doc?.credit_history || []).length > 0 && (
                    <div className="sm:col-span-2 text-xs border border-border rounded-sm divide-y divide-border" data-testid="cm-credit-history">
                      {doc.credit_history.map((h, i) => (
                        <div key={i} className="flex justify-between px-3 py-1.5">
                          <span>{h.date} · {h.kind === "Refund" ? `Refund via ${h.method}${h.reference ? ` (${h.reference})` : ""}` : `Applied to ${h.invoice_no}`} · {h.by}</span>
                          <span className="tabular font-semibold">{fmt(h.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              {docType === "Invoice" && doc?.amount_credited > 0 && (
                <Field label="Amount Credited"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular font-semibold text-amber-700" data-testid="inv-amount-credited">{fmt(doc.amount_credited)}</div></Field>
              )}
              <Field label="Currency">
                {readOnly ? <ReadOnly value={form.currency || "—"} /> : (
                  <Select value={form.currency || "none"} onValueChange={(v) => setF("currency", v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-sm" data-testid="card-currency"><SelectValue placeholder="Select currency" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <p className="overline-label col-span-2 xl:col-span-3 pt-2 border-t border-border/60 text-primary">Totals</p>
              <Field label="Total Sell Price Excl. GST"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular font-medium" data-testid="gen-subtotal">{fmt(subtotal)}</div></Field>
              <Field label={`${taxRate}% GST`}><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular" data-testid="gen-tax">{fmt(tax)}</div></Field>
              <Field label={`Total Sell Price Incl. GST${curCode ? ` (${curCode})` : ""}`}><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular font-bold text-primary" data-testid="gen-total">{fmt(total)}</div></Field>
              {showAud && (
                <Field label="Total in AUD"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular font-semibold" data-testid="gen-total-aud">{rateToAud ? moneyIn(totalAud, "AUD") : "Rate unavailable"}</div></Field>
              )}
              <Field label="Total Cost"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular" data-testid="gen-cost">{fmt(totalCost)}</div></Field>
              <Field label="GP %"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular font-semibold" data-testid="gen-gp">{gpPct.toFixed(1)}</div></Field>
            </div>
          </Section>

          <Section title="Lines" subtitle={isNew && !readOnly ? "at least one line with a description and quantity is required" : undefined}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="card-lines-table">
                <thead>
                  <tr className="text-left border-b border-border">
                    <th className="py-2 pr-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-14">Type</th>
                    <th className="py-2 pr-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold min-w-[220px]">Item / Description{isNew && !readOnly && <span className="text-red-500 ml-0.5">*</span>}</th>
                    <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-24 text-right">Quantity{isNew && !readOnly && <span className="text-red-500 ml-0.5">*</span>}</th>
                    {showQtyCols && <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-20 text-right">Shipped</th>}
                    {showQtyCols && <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-20 text-right">Invoiced</th>}
                    {showLead && <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-24 text-right">Lead Time (days)</th>}
                    {showLead && <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-28 text-right">Est. Delivery</th>}
                    {showMargin && <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-20 text-right">Margin</th>}
                    <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-28 text-right">Unit Price</th>
                    <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-28 text-right">Amount</th>
                    {!readOnly && <th className="w-8" />}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => (
                    <tr key={idx} className="border-b border-border/60" data-testid="card-line-row">
                      <td className="py-1.5 pr-2 text-xs text-muted-foreground">Item</td>
                      <td className="py-1.5 pr-2">
                        {readOnly ? (
                          <div className="flex flex-col">
                            <LinePartNo itemId={l.item_id} items={items} description={l.description} testid="card-line-part-no" />
                            <span data-testid="card-line-desc-text">{l.description}</span>
                            {!showLead && lineLead(l) ? <span className="text-[10px] text-muted-foreground" data-testid="card-line-leadtime">Lead time: {lineLead(l)} days</span> : null}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <ItemPicker value={l.item_id || ""} items={items} onChange={(v) => pickItem(idx, v)} placeholder="Select item (optional)" testid="card-line-item" />
                            <Input value={l.description} onChange={(e) => setLine(idx, "description", e.target.value)} onBlur={() => { const it = items.find((x) => x.id === l.item_id); if (it && !(l.description || "").trim()) setLine(idx, "description", itemLineDescription(it)); }} className={`rounded-sm h-8 text-sm ${isNew && !l.description ? "ring-1 ring-amber-400 border-amber-400" : ""}`} placeholder="Description" data-testid="card-line-desc" />
                            <UseItemDescription item={items.find((x) => x.id === l.item_id)} description={l.description} onUse={(d) => setLine(idx, "description", d)} testid="card-line-use-item-desc" />
                            {!showLead && lineLead(l) ? <span className="text-[10px] text-muted-foreground" data-testid="card-line-leadtime">Lead time: {lineLead(l)} days</span> : null}
                          </div>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-right">
                        {readOnly ? <span className="tabular">{l.quantity}</span> : <Input type="number" value={l.quantity} onChange={(e) => setLine(idx, "quantity", e.target.value)} className={`rounded-sm h-8 num text-right ${isNew && !(Number(l.quantity) > 0) ? "ring-1 ring-amber-400 border-amber-400" : ""}`} data-testid="card-line-qty" />}
                      </td>
                      {showQtyCols && <td className="py-1.5 px-2 text-right tabular text-xs text-muted-foreground" data-testid="card-line-shipped">{l.qty_shipped || 0}</td>}
                      {showQtyCols && <td className="py-1.5 px-2 text-right tabular text-xs text-muted-foreground" data-testid="card-line-invoiced">{l.qty_invoiced || 0}</td>}
                      {showLead && (
                        <td className="py-1.5 px-2 text-right">
                          {readOnly ? <span className="tabular" data-testid="card-line-lead-col">{lineLead(l) || "—"}</span> : <Input type="number" min={0} value={l.lead_time_days === "" ? "" : (Number(l.lead_time_days) || lineLead(l))} onChange={(e) => setLine(idx, "lead_time_days", e.target.value)} className="rounded-sm h-8 num text-right" data-testid="card-line-lead-input" />}
                        </td>
                      )}
                      {showLead && <td className="py-1.5 px-2 text-right tabular text-xs" data-testid="card-line-est-delivery">{estDelivery(l)}</td>}
                      {showMargin && <td className="py-1.5 px-2 text-right"><MarginBadge pct={lineMargin(l, items, rateToAud || 1)} threshold={marginThreshold} testid="card-line-margin" /></td>}
                      <td className="py-1.5 px-2 text-right">
                        {readOnly ? <span className="tabular">{money(l.unit_price)}</span> : <Input type="number" value={l.unit_price} onChange={(e) => setLine(idx, "unit_price", e.target.value)} className="rounded-sm h-8 num text-right" data-testid="card-line-price" />}
                      </td>
                      <td className="py-1.5 px-2 text-right tabular font-medium">{money(Number(l.quantity || 0) * Number(l.unit_price || 0))}</td>
                      {!readOnly && (
                        <td className="py-1.5 text-right">
                          {lines.length > 1 && <button onClick={() => removeLine(idx)} className="p-1 text-muted-foreground hover:text-destructive" data-testid="card-line-delete"><Trash size={15} /></button>}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!readOnly && (
              <button onClick={addLine} data-testid="card-add-line" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"><Plus size={14} weight="bold" /> Add line</button>
            )}
          </Section>

          <Section title="Shipping and Billing" mobileCollapsed>
            <div className="space-y-4">
              <Field label="Ship-to Name">
                {readOnly ? <ReadOnly value={form.ship_to_name} /> : <Input data-testid="card-ship-name" value={form.ship_to_name} onChange={(e) => setF("ship_to_name", e.target.value)} className="rounded-sm" />}
              </Field>
              {readOnly ? (
                <Field label="Ship-to Address">
                  <ReadOnly value={[form.ship_to_address, form.ship_to_address2, [form.ship_to_city, form.ship_to_state, form.ship_to_postcode].filter(Boolean).join(" "), form.ship_to_country].filter(Boolean).join(", ")} />
                </Field>
              ) : (
                <AddressFields values={form} onChange={setF} prefix="ship_to_" testidPrefix="card-ship" />
              )}
              <div className="pt-2 border-t border-border/60">
                <p className="overline-label text-primary mb-2">Billing Address</p>
                {readOnly ? (
                  <Field label="Bill-to Address">
                    <ReadOnly value={[form.bill_to_address, form.bill_to_address2, [form.bill_to_city, form.bill_to_state, form.bill_to_postcode].filter(Boolean).join(" "), form.bill_to_country].filter(Boolean).join(", ")} />
                  </Field>
                ) : (
                  <AddressFields values={form} onChange={setF} prefix="bill_to_" testidPrefix="card-bill" />
                )}
              </div>
              <div className="pt-1 border-t border-border/60">
                {readOnly ? (
                  <Field label="Incoterms">
                    <ReadOnly value={[form.incoterms, form.incoterms_location].filter(Boolean).join(" — ")} />
                  </Field>
                ) : (
                  <IncotermsFields incoterms={form.incoterms} location={form.incoterms_location} onChange={setF} testidPrefix="card" />
                )}
              </div>
            </div>
          </Section>

          <Section title="Freight Details" subtitle="Nominated freight forwarder" mobileCollapsed>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              <Field label="Name"><Input data-testid="freight-name" value={form.freight_name} disabled={readOnly} onChange={(e) => setF("freight_name", e.target.value)} className="rounded-sm" /></Field>
              <Field label="County/Country Code">
                <Select value={form.freight_country || "none"} disabled={readOnly} onValueChange={(v) => setF("freight_country", v === "none" ? "" : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="freight-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent className="max-h-72"><SelectItem value="none">— None —</SelectItem>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Account No."><Input data-testid="freight-account-no" value={form.freight_account_no} disabled={readOnly} onChange={(e) => setF("freight_account_no", e.target.value)} className="rounded-sm font-mono" /></Field>
              <Field label="Phone No."><Input data-testid="freight-phone" value={form.freight_phone} disabled={readOnly} onChange={(e) => setF("freight_phone", e.target.value)} className="rounded-sm" /></Field>
              <Field label="Address"><Input data-testid="freight-address" value={form.freight_address} disabled={readOnly} onChange={(e) => setF("freight_address", e.target.value)} className="rounded-sm" /></Field>
              <Field label="Fax No."><Input data-testid="freight-fax" value={form.freight_fax} disabled={readOnly} onChange={(e) => setF("freight_fax", e.target.value)} className="rounded-sm" /></Field>
              <Field label="Address 2"><Input data-testid="freight-address2" value={form.freight_address2} disabled={readOnly} onChange={(e) => setF("freight_address2", e.target.value)} className="rounded-sm" /></Field>
              <Field label="Telex No."><Input data-testid="freight-telex" value={form.freight_telex} disabled={readOnly} onChange={(e) => setF("freight_telex", e.target.value)} className="rounded-sm" /></Field>
              <Field label="Address 3"><Input data-testid="freight-address3" value={form.freight_address3} disabled={readOnly} onChange={(e) => setF("freight_address3", e.target.value)} className="rounded-sm" /></Field>
              <Field label="E-Mail"><Input data-testid="freight-email" value={form.freight_email} disabled={readOnly} onChange={(e) => setF("freight_email", e.target.value)} className="rounded-sm" /></Field>
              <Field label="Contact"><Input data-testid="freight-contact" value={form.freight_contact} disabled={readOnly} onChange={(e) => setF("freight_contact", e.target.value)} className="rounded-sm" /></Field>
              <Field label="Location Code">
                <Select value={form.freight_location_code || "none"} disabled={readOnly} onValueChange={(v) => setF("freight_location_code", v === "none" ? "" : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="freight-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.code}>{l.code} · {l.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Post Code/City"><Input data-testid="freight-postcode" value={form.freight_postcode} disabled={readOnly} onChange={(e) => setF("freight_postcode", e.target.value)} className="rounded-sm" /></Field>
              <Field label="Shipment Date"><Input type="date" data-testid="freight-shipment-date" value={form.freight_shipment_date} disabled={readOnly} onChange={(e) => setF("freight_shipment_date", e.target.value)} className="rounded-sm" /></Field>
              <Field label="City"><Input data-testid="freight-city" value={form.freight_city} disabled={readOnly} onChange={(e) => setF("freight_city", e.target.value)} className="rounded-sm" /></Field>
              <Field label="Shipping Advice">
                <Select value={form.freight_shipping_advice || "none"} disabled={readOnly} onValueChange={(v) => setF("freight_shipping_advice", v === "none" ? "" : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="freight-shipping-advice"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem><SelectItem value="Partial">Partial</SelectItem><SelectItem value="Complete">Complete</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="County"><Input data-testid="freight-county" value={form.freight_county} disabled={readOnly} onChange={(e) => setF("freight_county", e.target.value)} className="rounded-sm" /></Field>
              <Field label="Delivery Instruction"><Input data-testid="freight-delivery-instruction" value={form.freight_delivery_instruction} disabled={readOnly} onChange={(e) => setF("freight_delivery_instruction", e.target.value)} className="rounded-sm" /></Field>
            </div>
          </Section>

          <Section title="Totals" defaultOpen>
            <div className="max-w-sm ml-auto space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular font-medium">{fmt(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">GST</span>
                <span className="tabular text-xs" data-testid="card-gst-rule">
                  {!doc?.posted && form.customer_id && gstAuto === null ? <span className="text-destructive">Add a country to this customer</span>
                    : taxRate > 0 ? `${taxRate}% (Australia)` : "GST-free export"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">GST amount</span>
                <span className="tabular font-medium" data-testid="card-gst-amount">{fmt(tax)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-2 text-base font-bold">
                <span>Total{curCode ? ` (${curCode})` : ""}</span>
                <span className="tabular text-primary" data-testid="card-total">{fmt(total)}</span>
              </div>
              {showMargin && margin && (
                <div className="flex items-center justify-between text-xs" data-testid="card-margin-total">
                  <span className="text-muted-foreground">Margin (AUD, excl. GST)</span>
                  <span className={`tabular font-semibold ${lowMargin ? "text-red-600" : "text-emerald-700"}`}>{moneyIn(margin.profit, "AUD")} · {margin.pct.toFixed(1)}%</span>
                </div>
              )}
              {showAud && (
                <div className="flex items-center justify-between text-xs text-muted-foreground" data-testid="card-total-aud">
                  <span>≈ in AUD{rateToAud ? ` @ ${rateToAud.toFixed(4)}` : ""}</span>
                  <span className="tabular font-semibold text-foreground">{rateToAud ? moneyIn(totalAud, "AUD") : "Rate unavailable"}</span>
                </div>
              )}
            </div>
          </Section>
        </div>

        {/* Right: info pane */}
        <div className="lg:col-span-1">
          <div className="border border-border bg-card sticky top-4" data-testid="card-info-pane">
            <Tabs defaultValue="details">
              <TabsList className="w-full rounded-none border-b border-border bg-secondary/40 h-auto p-0">
                <TabsTrigger value="details" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="pane-tab-details"><ListChecks size={14} className="mr-1" /> Details</TabsTrigger>
                <TabsTrigger value="notes" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="pane-tab-notes"><NoteIcon size={14} className="mr-1" /> Notes</TabsTrigger>
                <TabsTrigger value="files" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="pane-tab-files"><Paperclip size={14} className="mr-1" /> Files</TabsTrigger>
                <TabsTrigger value="summary" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="pane-tab-summary"><Sparkle size={14} className="mr-1" /> Summary</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="p-4 space-y-2.5 text-sm mt-0">
                <PaneRow label="Document" value={isNew ? "— (unsaved)" : doc.doc_no} mono />
                <PaneRow label="Customer" value={form.customer_id ? (customers.find((c) => c.id === form.customer_id)?.name || doc?.customer_name) : "—"} />
                <PaneRow label="Status" value={status} />
                {docType === "Order" && !isNew && <PaneRow label="Shipping" value={doc?.shipping_status || "Not shipped"} />}
                <PaneRow label="External Ref" value={form.external_doc_no || "—"} mono />
                <div className="border-t border-border pt-2.5 space-y-2.5">
                  <PaneRow label="Currency" value={curCode || "—"} />
                  <PaneRow label="Subtotal" value={fmt(subtotal)} tabular />
                  <PaneRow label="GST" value={fmt(tax)} tabular />
                  <PaneRow label="Total" value={fmt(total)} tabular bold />
                  {showAud && <PaneRow label="Total (AUD)" value={rateToAud ? moneyIn(totalAud, "AUD") : "—"} tabular />}
                  {!isNew && (docType === "Order" || docType === "Invoice") && (
                    <>
                      <PaneRow label="Amount Paid" value={fmt(doc.amount_paid || 0)} tabular />
                      {doc.credit_applied > 0 && <PaneRow label="Credit Applied" value={fmt(doc.credit_applied)} tabular />}

                      <PaneRow label="Remaining" value={fmt((doc.total || 0) - (doc.amount_paid || 0) - (doc.credit_applied || 0))} tabular />
                    </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="notes" className="p-4 mt-0">
                <NotesPane docId={id} isNew={isNew} readOnly={viewOnly} notes={doc?.notes || []} onAdded={() => qc.invalidateQueries({ queryKey: ["sales-order", id] })} />
              </TabsContent>

              <TabsContent value="files" className="p-4 mt-0">
                <AttachmentsPane entityType="sales-order" entityId={id} isNew={isNew} readOnly={viewOnly} label="Upload Customer PO" />
              </TabsContent>

              <TabsContent value="summary" className="p-4 mt-0">
                <SummaryPane docId={id} isNew={isNew} existing={doc?.ai_summary} onDone={() => qc.invalidateQueries({ queryKey: ["sales-order", id] })} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <Dialog open={!!poResult} onOpenChange={(v) => { if (!v) setPoResult(null); }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="make-pos-result-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Storefront size={20} className="text-primary" /> Purchase Orders Created</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            {(poResult?.created || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No purchase orders were created.</p>
            ) : (
              <div className="border border-border divide-y divide-border max-h-56 overflow-y-auto">
                {(poResult?.created || []).map((c, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm" data-testid="make-pos-row">
                    <span className="font-mono text-xs font-medium">{c.doc_no}<span className="text-muted-foreground ml-1 font-sans">· {c.vendor_name} · {c.lines} line{c.lines === 1 ? "" : "s"}</span></span>
                    <span className="tabular font-medium">{money(c.total)}</span>
                  </div>
                ))}
              </div>
            )}
            {(poResult?.skipped || []).length > 0 && (
              <p className="text-xs text-amber-600" data-testid="make-pos-skipped">Skipped {poResult.skipped.length} line{poResult.skipped.length === 1 ? "" : "s"} with no assigned vendor: {poResult.skipped.join(", ")}. Set a Vendor No. on those items.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoResult(null)} className="rounded-sm">Close</Button>
            <Button onClick={() => { setPoResult(null); navigate(`/purchasing?tab=orders&so=${encodeURIComponent(doc.doc_no)}`); }} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="make-pos-view">View Purchase Orders</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Email confirmation dialog ---- */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="email-confirmation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EnvelopeSimple size={20} className="text-primary" /> Email {docType === "Order" ? "Order Confirmation" : DOC_LABEL[docType]}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Recipients */}
            <div className="space-y-3">
              <div>
                <Label className="overline-label">Send to</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Pick addresses on file for {doc?.customer_name || "this customer"}, or type any email below.</p>
              </div>
              <div className="border border-border rounded-sm divide-y divide-border max-h-44 overflow-y-auto" data-testid="email-recipient-list">
                {recipientOptions.length === 0 && (
                  <p className="text-sm text-muted-foreground p-3" data-testid="email-no-recipients">No email addresses on file. Type an address below.</p>
                )}
                {recipientOptions.map((o) => (
                  <label key={o.email} className="flex items-start gap-2.5 p-2.5 cursor-pointer hover:bg-secondary/40" data-testid="email-recipient-option">
                    <input
                      type="checkbox"
                      checked={selectedRecipients.includes(o.email)}
                      onChange={() => toggleRecipient(o.email)}
                      className="mt-0.5 accent-primary h-4 w-4"
                      data-testid={`email-recipient-checkbox-${o.email}`}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium truncate">{o.label}{o.primary && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-primary font-semibold">Primary</span>}</span>
                      <span className="block text-xs text-muted-foreground font-mono truncate">{o.email}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div>
                <Label className="overline-label" htmlFor="email-manual-input">Add any email address</Label>
                <Input
                  id="email-manual-input"
                  type="text"
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  placeholder="name@example.com, another@example.com"
                  className="rounded-sm mt-1"
                  data-testid="email-manual-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Separate multiple addresses with commas. These don't need to be on file.</p>
              </div>
            </div>

            {/* Attachment preview */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="overline-label">Attachment</Label>
                {!isNew && (
                  <button
                    onClick={() => window.open(`${API}/sales-orders/${id}/pdf`, "_blank")}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    data-testid="email-download-pdf"
                  >
                    <FilePdf size={14} weight="bold" /> Download PDF
                  </button>
                )}
              </div>
              <div className="border border-border rounded-sm overflow-hidden bg-secondary/30" style={{ height: 300 }}>
                {!isNew && (
                  <iframe
                    title="Order PDF preview"
                    src={`${API}/sales-orders/${id}/pdf`}
                    className="w-full h-full"
                    data-testid="email-pdf-preview"
                  />
                )}
              </div>
              <p className="text-[11px] text-muted-foreground font-mono truncate">{doc?.doc_no}.pdf — attached to this email</p>
            </div>

            {!isNew && <EmailAttachmentPicker entityType="sales-order" entityId={id} value={emailAttachIds} onChange={setEmailAttachIds} />}
            {!isNew && <EmailMessageBox templateBase={`/sales-email-template/${docType}`} recipientsKey={["so-email-recipients", id]} message={emailMessage} onChange={(v) => { setEmailMessage(v); setMessageTouched(true); }} templateMessage={emailRecipients?.message || ""} source={emailRecipients?.template_source} linesNote="The document lines, totals and PDF are always included." tagHint="{doc_number}" />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} className="rounded-sm" data-testid="email-cancel">Cancel</Button>
            <Button
              onClick={() => emailDoc.mutate()}
              disabled={totalRecipients === 0 || emailDoc.isPending}
              className="rounded-sm bg-primary hover:bg-primary/90"
              data-testid="email-send"
            >
              {emailDoc.isPending ? "Sending…" : `Send${totalRecipients ? ` (${totalRecipients})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- BC Post dialog (Ship / Invoice / Ship and Invoice) ---- */}
      <Dialog open={postOpen} onOpenChange={setPostOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="post-so-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck size={20} className="text-primary" /> Post Sales Order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-1.5 bg-secondary/50 p-1 rounded-sm w-fit">
              {[["ship", "Ship"], ["invoice", "Invoice"], ["ship_and_invoice", "Ship and Invoice"]].map(([v, lbl]) => (
                <button key={v} data-testid={`post-action-${v}`} onClick={() => changeAction(v)}
                  className={`px-3 py-1.5 rounded-sm text-xs font-semibold transition-colors ${postAction === v ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-card"}`}>{lbl}</button>
              ))}
            </div>
            <div className="border border-border rounded-sm overflow-x-auto">
              <table className="w-full text-sm" data-testid="post-lines-table">
                <thead className="bg-muted">
                  <tr className="text-left">
                    <th className="py-2 px-2 text-xs uppercase tracking-wider font-semibold">Description</th>
                    <th className="py-2 px-2 text-xs uppercase tracking-wider font-semibold text-right w-16">Qty</th>
                    <th className="py-2 px-2 text-xs uppercase tracking-wider font-semibold text-right w-16">Shipped</th>
                    <th className="py-2 px-2 text-xs uppercase tracking-wider font-semibold text-right w-16">Invoiced</th>
                    {(postAction === "ship" || postAction === "ship_and_invoice") && <th className="py-2 px-2 text-xs uppercase tracking-wider font-semibold text-right w-24">Qty to Ship</th>}
                    {(postAction === "invoice" || postAction === "ship_and_invoice") && <th className="py-2 px-2 text-xs uppercase tracking-wider font-semibold text-right w-24">Qty to Invoice</th>}
                  </tr>
                </thead>
                <tbody>
                  {postLines.map((l) => (
                    <tr key={l.line_id} className="border-t border-border" data-testid="post-line-row">
                      <td className="py-1.5 px-2">{l.description}</td>
                      <td className="py-1.5 px-2 text-right tabular">{l.quantity}</td>
                      <td className="py-1.5 px-2 text-right tabular text-muted-foreground">{l.qty_shipped}</td>
                      <td className="py-1.5 px-2 text-right tabular text-muted-foreground">{l.qty_invoiced}</td>
                      {(postAction === "ship" || postAction === "ship_and_invoice") && (
                        <td className="py-1.5 px-2 text-right">
                          <Input type="number" value={l.qty_to_ship} onChange={(e) => setPostLine(l.line_id, "qty_to_ship", e.target.value)} className="rounded-sm h-8 num text-right w-20 ml-auto" data-testid="post-qty-ship" />
                        </td>
                      )}
                      {(postAction === "invoice" || postAction === "ship_and_invoice") && (
                        <td className="py-1.5 px-2 text-right">
                          <Input type="number" value={l.qty_to_invoice} onChange={(e) => setPostLine(l.line_id, "qty_to_invoice", e.target.value)} className="rounded-sm h-8 num text-right w-20 ml-auto" data-testid="post-qty-invoice" />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">Shipping reduces inventory and creates a Posted Sales Shipment. Invoicing posts to receivables and creates a Posted Sales Invoice. You can only invoice quantities that have been shipped.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPostOpen(false)} className="rounded-sm" data-testid="post-cancel">Cancel</Button>
            <Button onClick={() => postSO.mutate()} disabled={postSO.isPending} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="post-confirm">
              {postSO.isPending ? "Posting…" : (postAction === "ship" ? "Ship" : postAction === "invoice" ? "Invoice" : "Ship and Invoice")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Record payment (posted invoice) ---- */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="sales-payment-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CurrencyDollar size={20} className="text-primary" /> Record Payment</DialogTitle></DialogHeader>
          {doc && (
            <div className="space-y-3">
              <div className="text-sm space-y-1 border border-border rounded-sm p-3 bg-secondary/30">
                <div className="flex justify-between"><span className="text-muted-foreground">Invoice total</span><span className="tabular">{money(doc.total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Already paid</span><span className="tabular">{money(doc.amount_paid || 0)}</span></div>
                {doc.credit_applied > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Credit applied</span><span className="tabular" data-testid="payment-credit-applied">{money(doc.credit_applied)}</span></div>}

                <div className="flex justify-between font-bold"><span>Remaining</span><span className="tabular text-primary">{money((doc.total || 0) - (doc.amount_paid || 0) - (doc.credit_applied || 0))}</span></div>
              </div>
              <div><Label className="overline-label">Amount</Label><Input type="number" step="0.01" data-testid="sales-payment-amount" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="mt-1 rounded-sm num" /></div>
              <div><Label className="overline-label">Method</Label>
                <Select value={payMethod} onValueChange={setPayMethod}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="sales-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Check">Check</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => recordPayment.mutate()} disabled={!payAmount || Number(payAmount) <= 0 || recordPayment.isPending} data-testid="sales-save-payment" className="rounded-sm bg-primary hover:bg-primary/90">{recordPayment.isPending ? "Saving…" : "Record Payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {docType === "CreditMemo" && !isNew && (
        <ReversePostedLinesDialog open={reverseOpen} onOpenChange={setReverseOpen} cmId={id} onDone={invalidate} />
      )}
      {docType === "CreditMemo" && doc?.posted && (
        <>
          <RefundCreditDialog open={creditAction === "refund"} onOpenChange={(v) => { if (!v) setCreditAction(null); }} cm={doc} onDone={invalidate} />
          <ApplyCreditDialog open={creditAction === "apply"} onOpenChange={(v) => { if (!v) setCreditAction(null); }} cm={doc} onDone={invalidate} />
        </>
      )}


      {delPosted !== null && (
        <Dialog open onOpenChange={(v) => { if (!v) setDelPosted(null); }}>
          <DialogContent data-testid="delete-posted-dialog">
            <DialogHeader><DialogTitle>Delete posted invoice {doc?.doc_no}?</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">This removes the invoice{doc?.amount_paid > 0 ? ` and its recorded payments (${money(doc.amount_paid)})` : ""}, reverses the customer balance{doc?.source_order_no ? `, deletes its posted shipment, returns shipped items to stock and reopens ${doc.source_order_no}` : " and deletes its posted shipment record (direct invoices don't change stock)"}. A copy is kept for audit. This cannot be undone.</p>
              <div>
                <Label className="overline-label" htmlFor="delete-reason">Reason for deletion <span className="text-red-500">*</span></Label>
                <textarea id="delete-reason" value={delPosted} onChange={(e) => setDelPosted(e.target.value)} rows={3} placeholder="e.g. Invoice raised against the wrong customer" className="mt-1 w-full rounded-sm border border-border p-2 text-sm outline-none focus:border-primary resize-none" data-testid="delete-posted-reason" />
                <p className="text-[11px] text-muted-foreground mt-1">At least 10 characters. Saved to the audit log.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDelPosted(null)} className="rounded-sm">Cancel</Button>
              <Button onClick={() => deletePosted.mutate()} disabled={delPosted.trim().length < 10 || deletePosted.isPending} className="rounded-sm bg-destructive hover:bg-destructive/90 text-white" data-testid="delete-posted-confirm">{deletePosted.isPending ? "Deleting…" : "Delete Invoice"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(v) => { if (!v) setConfirm(null); }}
        title={confirm?.kind === "post-cm" ? "Post this credit memo?" : confirm?.kind === "delete" ? "Delete this document?" : confirm?.kind === "order" ? "Convert quote to order?" : confirm?.kind === "correct" ? "Correct this posted invoice?" : confirm?.kind === "return" ? "Post this return order?" : "Convert order to invoice?"}
        description={confirm?.kind === "post-cm" ? `${doc?.doc_no}: ${money(total)} will be credited to the customer${doc?.applies_to_doc_no ? ` against ${doc.applies_to_doc_no} (any amount still owing is cleared first; the rest stays as customer credit)` : " as open customer credit"}.${form.return_to_stock !== false ? " Item quantities go back into stock." : ""} This cannot be undone.` : confirm?.kind === "delete" ? `${doc?.doc_no} will be permanently deleted.` : confirm?.kind === "correct" ? `A corrective credit memo will be posted to reverse ${doc?.doc_no}, and its source order will reopen so you can re-post it. To also return the goods to stock, undo the related shipment afterwards.` : confirm?.kind === "return" ? `${doc?.doc_no}: the returned goods go back into stock and a Posted Credit Memo is raised to credit the customer. This cannot be undone.` : `${doc?.doc_no} will be converted and archived. This cannot be undone.`}
        confirmLabel={confirm?.kind === "post-cm" ? "Post Credit Memo" : confirm?.kind === "delete" ? "Delete" : confirm?.kind === "order" ? "Convert to Order" : confirm?.kind === "correct" ? "Create Credit Memo" : confirm?.kind === "return" ? "Post Return" : "Convert to Invoice"}
        testid="card-confirm-dialog"
        onConfirm={() => {
          if (confirm?.kind === "post-cm") post.mutate();
          else if (confirm?.kind === "delete") del.mutate();
          else if (confirm?.kind === "order") convertToOrder.mutate();
          else if (confirm?.kind === "correct") correctInvoice.mutate();
          else if (confirm?.kind === "return") postReturn.mutate();
          else if (confirm?.kind === "invoice") convertToInvoice.mutate();
          setConfirm(null);
        }}
      />
    </div>
  );
}

function NotesPane({ docId, isNew, notes, onAdded, readOnly }) {
  const [text, setText] = useState("");
  const add = useMutation({
    mutationFn: async () => (await api.post(`/sales-orders/${docId}/notes`, { text })).data,
    onSuccess: () => { setText(""); onAdded(); toast.success("Note added"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  if (isNew) return <p className="text-sm text-muted-foreground">Save the document to add notes.</p>;
  return (
    <div className="space-y-3">
      <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="notes-list">
        {notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
        {notes.map((n) => (
          <div key={n.id} className="border border-border rounded-sm p-2.5 text-sm" data-testid="note-item">
            <p className="whitespace-pre-wrap">{n.text}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{n.created_by} · {(n.created_at || "").slice(0, 10)}</p>
          </div>
        ))}
      </div>
      {!readOnly && <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Add a note…" data-testid="note-input" className="w-full rounded-sm border border-border p-2 text-sm outline-none focus:border-primary resize-none" />}
      {!readOnly && <Button onClick={() => add.mutate()} disabled={!text.trim() || add.isPending} data-testid="add-note-button" className="rounded-sm bg-primary hover:bg-primary/90 h-8 text-xs w-full"><Plus size={14} className="mr-1" /> Add Note</Button>}
    </div>
  );
}

function SummaryPane({ docId, isNew, existing, onDone }) {
  const [summary, setSummary] = useState(existing || "");
  useEffect(() => { setSummary(existing || ""); }, [existing]);
  const gen = useMutation({
    mutationFn: async () => (await api.post(`/ai/summarize-sales-order/${docId}`)).data,
    onSuccess: (r) => { setSummary(r.summary); onDone(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate summary"),
  });
  if (isNew) return <p className="text-sm text-muted-foreground">Save the document to generate an Atlas summary.</p>;
  return (
    <div className="space-y-3">
      {summary ? (
        <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="ai-summary-text">{summary}</p>
      ) : (
        <p className="text-sm text-muted-foreground">Let Atlas summarize this document.</p>
      )}
      <Button onClick={() => gen.mutate()} disabled={gen.isPending} data-testid="generate-summary-button" className="rounded-sm bg-primary hover:bg-primary/90 h-8 text-xs w-full">
        {gen.isPending ? <><div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" /> Generating…</> : <><Sparkle size={14} weight="fill" className="mr-1" /> {summary ? "Regenerate" : "Generate"} Summary</>}
      </Button>
    </div>
  );
}
